import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { useStatuses } from "@/context/StatusContext";
import { useInbox } from "@/context/InboxContext";
import StatusTimeline from "@/components/StatusTimeline";
import ShipmentChain from "@/components/ShipmentChain";
import DirectionBadge from "@/components/DirectionBadge";
import PaymentBadge from "@/components/PaymentBadge";
import ReshipDialog from "@/components/ReshipDialog";
import ReversePickupDialog from "@/components/ReversePickupDialog";
import TriggerChooserDialog from "@/components/TriggerChooserDialog";
import LinkedCleanupDialog from "@/components/LinkedCleanupDialog";
import { useReship } from "@/context/ReshipContext";
import { useRvp } from "@/context/RvpContext";
import { buildWhatsappUrl } from "@/lib/whatsapp";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { AtSign } from "lucide-react";
import { toast } from "sonner";
import {
  ArrowLeft, ExternalLink, MessageCircle, Send, Loader2, Plus, Trash2, MapPin, Truck, CalendarClock, Undo2, Phone, Paperclip, FileText, Download, X, CreditCard,
} from "lucide-react";

function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" }); } catch { return "—"; }
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" }); } catch { return "—"; }
}

export default function ShipmentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const goBack = () => { if (location.key && location.key !== "default") navigate(-1); else navigate("/shipments"); };
  const [s, setS] = useState(null);
  const [comment, setComment] = useState("");
  const [canned, setCanned] = useState([]);
  const [posting, setPosting] = useState(false);

  const { statuses, stages, stageMap, metaMap } = useStatuses();
  const { refresh: refreshInbox } = useInbox();
  const { refresh: refreshReship } = useReship();
  const { refresh: refreshRvp } = useRvp();
  const [reshipFor, setReshipFor] = useState(null);
  const [rvpOpen, setRvpOpen] = useState(false);
  const [reshipAfterRvp, setReshipAfterRvp] = useState(false);
  const [rvpAfterReship, setRvpAfterReship] = useState(false);
  const [chooserOpen, setChooserOpen] = useState(false);
  const [cleanup, setCleanup] = useState(null);
  const [attachFiles, setAttachFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const authToken = localStorage.getItem("gir_token");
  const fileHref = (att, dl) => `${process.env.REACT_APP_BACKEND_URL}${att.url}?auth=${encodeURIComponent(authToken || "")}${dl ? "&dl=1" : ""}`;
  const [team, setTeam] = useState([]);
  const [tagged, setTagged] = useState([]);
  const commentRef = useRef(null);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionStart, setMentionStart] = useState(0);
  const [mentionIndex, setMentionIndex] = useState(0);
  const load = useCallback(() => api.get(`/shipments/${id}`).then((r) => setS(r.data)).catch(() => setS(false)), [id]);
  const loadCanned = useCallback(() => api.get("/canned-responses").then((r) => setCanned(r.data)).catch(() => {}), []);
  useEffect(() => { load(); loadCanned(); }, [load, loadCanned]);
  useEffect(() => { api.get("/team").then((r) => setTeam(r.data)).catch(() => {}); }, []);

  if (s === false) return <div className="p-8 text-muted-foreground">Shipment not found. <Button variant="link" onClick={() => navigate("/shipments")}>Back</Button></div>;
  if (!s) return <div className="flex items-center justify-center h-96 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…</div>;

  const applyStatus = async (v) => {
    try {
      await api.put(`/shipments/${id}`, { status: v });
      load();
      refreshReship();
      refreshRvp();
      setReshipAfterRvp(false);
      setRvpAfterReship(false);
      const meta = metaMap?.[v] || {};
      if (meta.triggers_rvp && meta.triggers_reship) {
        setChooserOpen(true);
        return;
      }
      if (meta.triggers_rvp) {
        setRvpOpen(true);
        toast.info("Reverse pickup requested — add details or skip for now");
        return;
      }
      if (meta.triggers_reship) {
        setReshipFor(s);
        toast.info("New shipment requested — add details or skip for now");
        return;
      }
      toast.success("Final action updated");
    } catch { toast.error("Failed to update final action"); }
  };
  const changeStatus = async (v) => {
    const meta = metaMap?.[v] || {};
    const dropReship = !!(s.reshipped || s.reship_pending) && !meta.triggers_reship;
    const dropRvp = !!s.is_reverse_pickup && !meta.triggers_rvp;
    if (dropReship || dropRvp) { setCleanup({ status: v, dropReship, dropRvp }); return; }
    applyStatus(v);
  };
  const confirmCleanup = async (doDelete) => {
    const c = cleanup;
    setCleanup(null);
    if (!c) return;
    if (doDelete) {
      if (c.dropReship) await api.post(`/shipments/${id}/reship/cancel`).catch(() => {});
      if (c.dropRvp) await api.post(`/shipments/${id}/reverse-pickup/remove`).catch(() => {});
      toast.success("Linked follow-up deleted");
    }
    await applyStatus(c.status);
  };
  const handleChoose = (choice) => {
    setChooserOpen(false);
    if (choice === "rvp_first") { setReshipAfterRvp(true); setRvpOpen(true); toast.info("Reverse pickup first — new shipment opens next"); }
    else if (choice === "reship_first") { setRvpAfterReship(true); setReshipFor(s); toast.info("New shipment first — reverse pickup opens next"); }
    else { toast.info("Both kept pending — find them in the Reverse Pickup & New Shipments queues"); }
  };
  const changeStage = async (v) => {
    try {
      await api.put(`/shipments/${id}`, { stage: v });
      load();
      refreshReship();
      if (stageMap?.[v]?.triggers_reship) {
        setReshipFor(s);
        toast.info("New shipment requested — add details or skip for now");
        return;
      }
      toast.success("Status updated");
    } catch { toast.error("Failed to update status"); }
  };
  const assign = async (v) => {
    try { await api.put(`/shipments/${id}`, { assigned_to: v === "none" ? "" : v }); toast.success("Assignment updated"); refreshInbox(); load(); }
    catch { toast.error("Failed to update assignment"); }
  };
  const changePayment = async (v) => {
    try { await api.put(`/shipments/${id}`, { payment_method: v }); toast.success("Payment method updated"); load(); }
    catch { toast.error("Failed to update payment method"); }
  };

  const mentionMatches = mentionOpen
    ? team.filter((m) => m.name.toLowerCase().includes(mentionQuery.toLowerCase())).slice(0, 6)
    : [];

  const handleCommentChange = (e) => {
    const val = e.target.value;
    setComment(val);
    const pos = e.target.selectionStart;
    const before = val.slice(0, pos);
    const m = before.match(/(?:^|\s)@([^\s@]*)$/);
    if (m) {
      setMentionQuery(m[1]);
      setMentionStart(pos - m[1].length - 1);
      setMentionIndex(0);
      setMentionOpen(true);
    } else {
      setMentionOpen(false);
    }
  };

  const insertMention = (member) => {
    const after = comment.slice(mentionStart + 1 + mentionQuery.length);
    const before = comment.slice(0, mentionStart);
    const newVal = `${before}@${member.name} ${after}`;
    setComment(newVal);
    setTagged((t) => (t.includes(member.id) ? t : [...t, member.id]));
    setMentionOpen(false);
    setTimeout(() => commentRef.current?.focus(), 0);
  };

  const handleCommentKeyDown = (e) => {
    if (!mentionOpen || mentionMatches.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setMentionIndex((i) => (i + 1) % mentionMatches.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setMentionIndex((i) => (i - 1 + mentionMatches.length) % mentionMatches.length); }
    else if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); insertMention(mentionMatches[mentionIndex]); }
    else if (e.key === "Escape") { setMentionOpen(false); }
  };

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        if (f.size > 15 * 1024 * 1024) { toast.error(`${f.name} is larger than 15 MB`); continue; }
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/shipments/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setAttachFiles((a) => [...a, data]);
      }
    } catch { toast.error("Attachment upload failed"); }
    finally { setUploading(false); }
  };

  const addComment = async () => {
    if (!comment.trim() && attachFiles.length === 0) return;
    setPosting(true);
    try {
      await api.post(`/shipments/${id}/comments`, { text: comment.trim(), tagged_user_ids: tagged, attachments: attachFiles });
      setComment("");
      setTagged([]);
      setAttachFiles([]);
      if (tagged.length) refreshInbox();
      load();
    } catch (e) { toast.error("Failed to add comment"); }
    finally { setPosting(false); }
  };

  const comments = [...(s.comments || [])].reverse();

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" data-testid="detail-back" onClick={goBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-extrabold text-[#111411] tracking-tight flex items-center gap-2" data-testid="detail-order-id">
              Order {s.order_id}
              <DirectionBadge direction={s.direction} />
              <PaymentBadge method={s.payment_method} />
            </h1>
            <p className="text-sm text-muted-foreground">AWB {s.tracking_number}</p>
            {s.phone && (
              <a href={`https://wa.me/${String(s.phone).replace(/\D/g, "")}`} target="_blank" rel="noreferrer"
                 data-testid="detail-phone" className="text-sm text-[#0B3D3D] hover:text-[#1FA855] flex items-center gap-1.5 mt-0.5 w-fit">
                <Phone className="h-3.5 w-3.5" /> {s.phone}
              </a>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => window.open(buildWhatsappUrl([s]), "_blank")} data-testid="detail-whatsapp"
                  className="gap-2 bg-[#1FA855] hover:bg-[#178a45] text-white">
            <MessageCircle className="h-4 w-4" /> WhatsApp
          </Button>
          <Button variant="outline" onClick={() => setRvpOpen(true)} data-testid="detail-mark-rvp" className="gap-2">
            <Undo2 className="h-4 w-4" /> {s.is_reverse_pickup ? "Edit reverse pickup" : "Mark reverse pickup"}
          </Button>
          {s.tracking_link && (
            <a href={s.tracking_link} target="_blank" rel="noreferrer">
              <Button variant="outline" className="gap-2" data-testid="detail-track"><ExternalLink className="h-4 w-4" /> Track</Button>
            </a>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-lg border border-border bg-white p-5">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <Field icon={Truck} label="Courier">{s.courier_partner}</Field>
              <Field icon={MapPin} label="Destination">{[s.city, s.state].filter(Boolean).join(", ") || "—"}<div className="text-xs text-muted-foreground">{s.zip_code} {s.country}</div></Field>
              <Field icon={CalendarClock} label="Dispatch date">{fmtDate(s.pickup_date)}</Field>
              <div>
                <p className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1.5"><CreditCard className="h-3.5 w-3.5" /> Payment</p>
                <Select value={s.payment_method || ""} onValueChange={changePayment}>
                  <SelectTrigger className="h-9" data-testid="detail-payment"><SelectValue placeholder="Set payment" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="prepaid">Prepaid</SelectItem>
                    <SelectItem value="cod">COD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Field icon={CalendarClock} label="Initial EDD">{fmtDate(s.initial_edd)}</Field>
              <Field icon={Truck} label="TAT zone">{s.tat_zone || "—"}</Field>
              <Field icon={CalendarClock} label="TAT est. delivery">{fmtDate(s.tat_estimated_date)}</Field>
              <div>
                <p className="text-xs text-muted-foreground mb-1.5">Status</p>
                <Select value={s.stage || "open"} onValueChange={changeStage}>
                  <SelectTrigger className="h-9" data-testid="detail-stage"><SelectValue /></SelectTrigger>
                  <SelectContent>{stages.map((st) => <SelectItem key={st.key} value={st.key}>{st.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1.5">Final action required</p>
                <Select value={s.status} onValueChange={changeStatus}>
                  <SelectTrigger className="h-9" data-testid="detail-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{statuses.map((st) => <SelectItem key={st.key} value={st.key}>{st.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1.5">Assigned to</p>
                <Select value={s.assigned_to || "none"} onValueChange={assign}>
                  <SelectTrigger className="h-9" data-testid="detail-assignee"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {team.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Field icon={CalendarClock} label="Resolution deadline">
                <span className={s.is_overdue ? "text-[#C24127] font-semibold" : ""}>{fmtDate(s.resolution_date)}</span>
                {s.is_overdue && <div className="text-xs text-[#C24127]">Overdue</div>}
              </Field>
              <Field icon={CalendarClock} label="Days open">{s.days_since_dispatch ?? "—"}</Field>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-white p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-[#111411]">Activity & comments</h3>
              <ManageCanned canned={canned} onChange={loadCanned} />
            </div>

            <div className="space-y-3">
              {canned.length > 0 && (
                <Select onValueChange={(v) => { const c = canned.find((x) => x.id === v); if (c) setComment((prev) => prev ? `${prev}\n${c.text}` : c.text); }}>
                  <SelectTrigger className="w-full sm:w-72" data-testid="detail-canned-select">
                    <SelectValue placeholder="Insert a canned response…" />
                  </SelectTrigger>
                  <SelectContent>
                    {canned.map((c) => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <div className="relative">
                <Textarea data-testid="detail-comment-input" value={comment}
                          ref={commentRef}
                          onChange={handleCommentChange}
                          onKeyDown={handleCommentKeyDown}
                          rows={3} placeholder="Add a note… type @ to tag a teammate" />
                {mentionOpen && mentionMatches.length > 0 && (
                  <div className="absolute z-20 left-0 right-0 sm:right-auto sm:w-72 mt-1 rounded-md border border-border bg-white shadow-lg overflow-hidden"
                       data-testid="mention-dropdown">
                    <p className="text-[11px] text-muted-foreground px-3 py-1.5 border-b border-border">Tag a teammate — they&apos;ll be notified</p>
                    <div className="max-h-56 overflow-auto py-1">
                      {mentionMatches.map((m, i) => (
                        <button key={m.id} type="button" data-testid={`mention-opt-${m.id}`}
                                onMouseDown={(e) => { e.preventDefault(); insertMention(m); }}
                                onMouseEnter={() => setMentionIndex(i)}
                                className={`w-full text-left flex items-center gap-2 px-3 py-1.5 text-sm ${i === mentionIndex ? "bg-[#F0F2F0]" : "hover:bg-[#F0F2F0]"}`}>
                          <AtSign className="h-3.5 w-3.5 text-[#0B3D3D] shrink-0" />
                          <span className="truncate">{m.name} <span className="text-xs text-muted-foreground capitalize">· {m.role}</span></span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {attachFiles.length > 0 && (
                <div className="flex flex-wrap gap-2" data-testid="pending-attachments">
                  {attachFiles.map((a) => (
                    <span key={a.id} className="inline-flex items-center gap-1.5 text-xs bg-[#EEF2EE] border border-border rounded-full pl-2.5 pr-1 py-1">
                      <Paperclip className="h-3 w-3" /> <span className="max-w-[160px] truncate">{a.filename}</span>
                      <button type="button" data-testid={`remove-attach-${a.id}`} onClick={() => setAttachFiles((f) => f.filter((x) => x.id !== a.id))} className="hover:text-[#C24127] rounded-full leading-none"><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex items-center justify-between gap-2 flex-wrap">
                {tagged.length > 0 ? (
                  <div className="flex flex-wrap items-center gap-1.5" data-testid="detail-tagged-list">
                    {tagged.map((uid) => {
                      const m = team.find((x) => x.id === uid);
                      return (
                        <span key={uid} className="inline-flex items-center gap-1 text-xs bg-[#E4ECF7] text-[#1D4E89] rounded-full pl-2 pr-1 py-0.5">
                          @{m?.name || "user"}
                          <button type="button" data-testid={`untag-${uid}`} onClick={() => setTagged((t) => t.filter((x) => x !== uid))}
                                  className="hover:text-[#C24127] rounded-full leading-none">×</button>
                        </span>
                      );
                    })}
                  </div>
                ) : <span className="text-xs text-muted-foreground">Type <span className="font-semibold">@</span> to tag a teammate</span>}
                <div className="flex items-center gap-2">
                  <input ref={fileInputRef} type="file" multiple accept="image/*,application/pdf" className="hidden" data-testid="attach-input" onChange={onPickFiles} />
                  <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="attach-btn" className="gap-2">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />} Attach
                  </Button>
                  <Button onClick={addComment} disabled={posting || (!comment.trim() && attachFiles.length === 0)} data-testid="detail-add-comment"
                          className="gap-2 bg-[#0B3D3D] hover:bg-[#0A2E2E] text-white">
                    {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Post comment
                  </Button>
                </div>
              </div>
            </div>

            <div className="mt-6 space-y-4" data-testid="detail-comments">
              {comments.length === 0 ? (
                <p className="text-sm text-muted-foreground">No comments yet. Log the first update above.</p>
              ) : comments.map((c) => (
                <div key={c.id} className="flex gap-3" data-testid={`comment-${c.id}`}>
                  <div className="h-8 w-8 rounded-full bg-[#E8ECE8] text-[#0B3D3D] flex items-center justify-center text-xs font-bold shrink-0">
                    {(c.author_name || "?").slice(0, 1).toUpperCase()}
                  </div>
                  <div className={`flex-1 ${c.is_note ? "bg-[#FBF3EC] border border-[#F0D8C4] rounded-lg p-3" : ""}`}
                       data-testid={c.is_note ? `note-${c.id}` : undefined}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-[#111411]">{c.author_name || "Unknown"}</span>
                      {c.is_note && <span className="text-[10px] font-semibold uppercase tracking-wide text-[#B45309] bg-[#F7E4D2] rounded px-1.5 py-0.5">Note</span>}
                      <span className="text-xs text-muted-foreground">{fmtDateTime(c.created_at)}</span>
                    </div>
                    <p className="text-sm text-[#333] whitespace-pre-line mt-0.5">{c.text}</p>
                    {(c.attachments || []).length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2" data-testid={`comment-attachments-${c.id}`}>
                        {c.attachments.map((att) => (
                          <div key={att.id} className="inline-flex items-center gap-1.5 bg-[#F7FAF8] border border-border rounded-lg p-1.5" data-testid={`attachment-${att.id}`}>
                            {String(att.content_type || "").startsWith("image/") ? (
                              <a href={fileHref(att, false)} target="_blank" rel="noreferrer">
                                <img src={fileHref(att, false)} alt={att.filename} className="h-14 w-14 object-cover rounded" />
                              </a>
                            ) : (
                              <a href={fileHref(att, false)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-[#0B3D3D] hover:underline max-w-[180px]">
                                <FileText className="h-4 w-4 shrink-0" /> <span className="truncate">{att.filename}</span>
                              </a>
                            )}
                            <a href={fileHref(att, true)} data-testid={`download-${att.id}`} title="Download"
                               className="h-6 w-6 flex items-center justify-center rounded hover:bg-[#E7F0EC] text-[#0B3D3D]">
                              <Download className="h-3.5 w-3.5" />
                            </a>
                          </div>
                        ))}
                      </div>
                    )}
                    {(c.tagged_user_ids || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`comment-tags-${c.id}`}>
                        {c.tagged_user_ids.map((uid) => {
                          const m = team.find((x) => x.id === uid);
                          return <span key={uid} className="text-xs bg-[#E4ECF7] text-[#1D4E89] rounded-full px-2 py-0.5">@{m?.name || "user"}</span>;
                        })}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-6 h-fit">
          <ShipmentChain shipmentId={id} />
          <div className="rounded-lg border border-border bg-white p-5">
            <h3 className="font-heading font-bold text-[#111411] mb-4">History</h3>
            <StatusTimeline history={s.status_history} />
          </div>
        </div>
      </div>
      <ReshipDialog shipment={reshipFor} onClose={() => setReshipFor(null)}
                    onDone={() => { load(); refreshReship(); if (rvpAfterReship) { setRvpAfterReship(false); setRvpOpen(true); } }} />
      <ReversePickupDialog open={rvpOpen} onOpenChange={setRvpOpen} existing={s}
                           onDone={() => { load(); refreshRvp(); if (reshipAfterRvp) { setReshipAfterRvp(false); setReshipFor(s); } }} />
      <TriggerChooserDialog open={chooserOpen} onOpenChange={setChooserOpen} orderId={s.order_id} onChoose={handleChoose} />
      <LinkedCleanupDialog open={!!cleanup} onOpenChange={(o) => !o && setCleanup(null)} orderId={s.order_id}
                           dropReship={cleanup?.dropReship} dropRvp={cleanup?.dropRvp} onDecision={confirmCleanup} />
    </div>
  );
}

function Field({ icon: Icon, label, children }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><Icon className="h-3.5 w-3.5" /> {label}</p>
      <div className="text-[#111411] font-medium">{children}</div>
    </div>
  );
}

function ManageCanned({ canned, onChange }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");

  const add = async () => {
    if (!title.trim() || !text.trim()) { toast.error("Title and text required"); return; }
    try { await api.post("/canned-responses", { title, text }); toast.success("Canned response added"); setTitle(""); setText(""); onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  const remove = async (id) => { await api.delete(`/canned-responses/${id}`); onChange(); };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2" data-testid="manage-canned"><Plus className="h-4 w-4" /> Canned responses</Button>
      </DialogTrigger>
      <DialogContent data-testid="canned-dialog" className="max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">Canned responses</DialogTitle></DialogHeader>
        <div className="space-y-2 mb-4">
          {canned.length === 0 && <p className="text-sm text-muted-foreground">No templates yet.</p>}
          {canned.map((c) => (
            <div key={c.id} className="flex items-start justify-between gap-2 border border-border rounded-md p-2">
              <div><p className="text-sm font-medium">{c.title}</p><p className="text-xs text-muted-foreground">{c.text}</p></div>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-[#C24127]" onClick={() => remove(c.id)} data-testid={`del-canned-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
        <div className="space-y-2 border-t border-border pt-4">
          <Label>New template</Label>
          <Input placeholder="Title (e.g. Reattempt asked)" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="canned-title" />
          <Textarea placeholder="Response text…" rows={3} value={text} onChange={(e) => setText(e.target.value)} data-testid="canned-text" />
        </div>
        <DialogFooter>
          <Button onClick={add} data-testid="canned-add" className="bg-[#0B3D3D] hover:bg-[#0A2E2E] text-white">Add template</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
